import { authStore } from '../auth/auth-store';
import { getAuthClient } from '../auth/registry';
import { getRuntimeConfig } from '../config/runtime-config';
import { workspaceHeaders } from '../session/workspace';
import { createApiClient, type ApiClient, type ApiClientOptions } from './client';

let overrides: ApiClientOptions | null = null;
let instance: ApiClient | null = null;
let instanceKey: { config: unknown; overrides: unknown } | null = null;

/**
 * L-01 (ADR-0047): the server resolves text authored in several languages (process experiences, shipment profiles,
 * notifications it renders on request) for the viewer, so every request says which language the viewer reads — the
 * one the I18nProvider has put on `<html lang>`.
 */
function requestHeaders(): Record<string, string> {
  const lang = typeof document !== 'undefined' ? document.documentElement.lang : '';
  return lang ? { ...workspaceHeaders(), 'Accept-Language': lang } : workspaceHeaders();
}

function defaultOptions(): ApiClientOptions {
  return {
    baseUrl: getRuntimeConfig().apiBaseUrl,
    getDynamicHeaders: requestHeaders,
    getAccessToken: () => {
      const client = getAuthClient();
      if (client) return client.getAccessToken();
      return authStore.getState().accessToken;
    },
    onUnauthorized: async () => {
      // The request already retried once with a fresh token. A 401 now means the session is gone: try one renewal
      // (refresh token, then the identity provider's SSO session); if that fails, keep the screen and ask the user
      // to sign in again instead of dropping them on the login page mid-task.
      const state = authStore.getState();
      if (state.status !== 'authenticated') return;
      const client = getAuthClient();
      const renewed = client ? await client.refresh() : null;
      if (!renewed) authStore.getState().expire();
    },
  };
}

function resolveInstance(): ApiClient {
  const config = getRuntimeConfig();
  if (instance && instanceKey && instanceKey.config === config && instanceKey.overrides === overrides) return instance;
  instance = createApiClient({ ...defaultOptions(), ...(overrides ?? {}) });
  instanceKey = { config, overrides };
  return instance;
}

/**
 * Creates a lazily-initialised client that delegates to a fresh instance whenever the factory's
 * inputs change. Lets `apiClient` be imported before runtime configuration is available.
 */
export function createLazyApiClient(resolve: () => ApiClient): ApiClient {
  return {
    get baseUrl() {
      return resolve().baseUrl;
    },
    get: (path, opts) => resolve().get(path, opts),
    post: (path, body, opts) => resolve().post(path, body, opts),
    put: (path, body, opts) => resolve().put(path, body, opts),
    patch: (path, body, opts) => resolve().patch(path, body, opts),
    delete: (path, opts) => resolve().delete(path, opts),
    request: (method, path, body, opts) => resolve().request(method, path, body, opts),
    sse: (path, opts) => resolve().sse(path, opts),
  };
}

/**
 * Default client bound to `getRuntimeConfig().apiBaseUrl` and the auth session (OIDC client when
 * installed, else the auth store's access token). A 401 marks the session anonymous.
 */
export const apiClient: ApiClient = createLazyApiClient(resolveInstance);

/** Overrides options of the default client (e.g. a custom fetch in tests or a different base URL). */
export function configureDefaultApiClient(options: ApiClientOptions): void {
  overrides = { ...(overrides ?? {}), ...options };
  instance = null;
}

/** Drops overrides and the cached instance (tests). */
export function resetDefaultApiClient(): void {
  overrides = null;
  instance = null;
  instanceKey = null;
}
